import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { isAutoRefund } from "@/lib/refundReason";
import RefundReasonBadge from "@/components/feature/RefundReasonBadge";

interface CreditTransaction {
  id: string;
  type: "charge" | "use" | "bonus" | "refund" | "admin_grant" | "admin_deduct";
  amount: number;
  balance_after: number;
  description: string | null;
  refund_reason: string | null;
  payment_method: string | null;
  status: string | null;
  admin_notes: string | null;
  created_at: string;
}

const TYPE_CONFIG = {
  charge: { label: "충전", icon: "ri-add-circle-line", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", sign: "+" },
  use: { label: "사용", icon: "ri-send-plane-line", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", sign: "" },
  bonus: { label: "보너스", icon: "ri-gift-line", color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/20", sign: "+" },
  refund: { label: "환불", icon: "ri-refund-2-line", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", sign: "+" },
  admin_grant: { label: "관리자 지급", icon: "ri-shield-check-line", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20", sign: "+" },
  admin_deduct: { label: "관리자 차감", icon: "ri-shield-flash-line", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20", sign: "" },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: "승인 대기", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  approved: { label: "승인 완료", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  rejected: { label: "거절", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bank: "무통장 입금",
};

// 환불 사유 배지는 공용 규칙(@/lib/refundReason + RefundReasonBadge)을 사용한다.

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const PAGE_SIZE = 10;

export default function CreditTab() {
  const { user, perMessageRate } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [credit, setCredit] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const [txResult, creditResult] = await Promise.all([
        supabase
          .from("credit_transactions")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
        supabase.rpc("get_my_credit"),
      ]);

      if (txResult.error) {
        console.error("[CreditTab] credit_transactions 오류:", txResult.error.message);
        setError(txResult.error.message);
      } else {
        setTransactions((txResult.data ?? []) as CreditTransaction[]);
      }

      if (creditResult.error) {
        console.error("[CreditTab] credit 조회 오류:", creditResult.error.message);
      } else if (creditResult.data !== null && creditResult.data !== undefined) {
        setCredit(creditResult.data);
      }
    } catch (err) {
      console.error("[CreditTab] 조회 예외:", err);
      setError(err instanceof Error ? err.message : "알 수 없는 오류가 발생했습니다");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const filtered = transactions.filter((t) => typeFilter === "ALL" || t.type === typeFilter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalCharged = transactions.filter((t) => t.type === "charge" && t.status === "approved").reduce((s, t) => s + t.amount, 0);
  const totalUsed = transactions.filter((t) => t.type === "use").reduce((s, t) => s + Math.abs(t.amount), 0);
  const totalBonus = transactions.filter((t) => t.type === "bonus").reduce((s, t) => s + t.amount, 0);
  const pendingCharge = transactions.filter((t) => t.type === "charge" && t.status === "pending").reduce((s, t) => s + t.amount, 0);

  const autoRefundTxs = transactions.filter((t) => t.type === "refund" && isAutoRefund(t.refund_reason, t.description));
  const autoRefundCount = autoRefundTxs.length;
  const autoRefundAmount = autoRefundTxs.reduce((s, t) => s + Math.abs(t.amount), 0);

  return (
    <div className="space-y-6 md:space-y-7">
      {/* Header */}
      <div>
        <h2 className="text-white font-bold text-base md:text-lg">크레딧 내역</h2>
        <p className="text-gray-500 text-xs md:text-sm mt-0.5">충전 및 사용 내역을 확인합니다</p>
      </div>

      {/* 자동 환불 요약 */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 bg-amber-500/[0.06] border border-amber-500/20 rounded-2xl px-4 md:px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/15 shrink-0">
            <i className="ri-refund-2-line text-amber-400 text-lg"></i>
          </div>
          <div>
            <p className="text-amber-300 text-sm font-semibold">자동 환불 내역</p>
            <p className="text-gray-500 text-xs mt-0.5">발송 실패·타임아웃·예약 취소로 자동 환불된 내역입니다</p>
          </div>
        </div>
        <div className="flex items-center gap-5 sm:gap-7 sm:ml-auto">
          <div className="text-center">
            {loading ? (
              <div className="h-6 w-12 bg-white/[0.06] rounded animate-pulse mx-auto"></div>
            ) : (
              <p className="text-white text-lg font-bold leading-tight">{autoRefundCount.toLocaleString()}건</p>
            )}
            <p className="text-gray-500 text-[11px] mt-0.5">환불 건수</p>
          </div>
          <div className="w-px h-8 bg-amber-500/20"></div>
          <div className="text-center">
            {loading ? (
              <div className="h-6 w-20 bg-white/[0.06] rounded animate-pulse mx-auto"></div>
            ) : (
              <p className="text-emerald-400 text-lg font-bold leading-tight">₩{autoRefundAmount.toLocaleString()}</p>
            )}
            <p className="text-gray-500 text-[11px] mt-0.5">환불 금액</p>
          </div>
        </div>
      </div>

      {/* 잔액 + 통계 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "현재 잔액",
            value: loading ? "..." : `${Math.floor(credit / (perMessageRate ?? 20)).toLocaleString()}건`,
            sub: loading ? "" : `₩${credit.toLocaleString()}`,
            icon: "ri-wallet-3-line",
            gradient: "from-violet-500/20 to-violet-600/10",
            iconBg: "from-violet-500 to-violet-600",
          },
          {
            label: "총 충전액",
            value: `₩${totalCharged.toLocaleString()}`,
            sub: `${transactions.filter((t) => t.type === "charge" && t.status === "approved").length}회 승인 완료`,
            icon: "ri-add-circle-line",
            gradient: "from-emerald-500/20 to-emerald-600/10",
            iconBg: "from-emerald-500 to-teal-500",
          },
          {
            label: "총 사용액",
            value: `₩${totalUsed.toLocaleString()}`,
            sub: `${transactions.filter((t) => t.type === "use").length}회 차감`,
            icon: "ri-send-plane-line",
            gradient: "from-red-500/20 to-red-600/10",
            iconBg: "from-red-500 to-rose-500",
          },
          {
            label: "승인 대기",
            value: `₩${pendingCharge.toLocaleString()}`,
            sub: `${transactions.filter((t) => t.type === "charge" && t.status === "pending").length}건 처리중`,
            icon: "ri-time-line",
            gradient: "from-amber-500/20 to-amber-600/10",
            iconBg: "from-amber-500 to-orange-500",
          },
        ].map((s) => (
          <div key={s.label} className={`bg-gradient-to-br ${s.gradient} border border-white/[0.08] rounded-2xl p-4`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-xs">{s.label}</p>
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${s.iconBg} shrink-0`}>
                <i className={`${s.icon} text-white text-sm`}></i>
              </div>
            </div>
            {loading ? (
              <div className="h-6 w-20 bg-white/[0.06] rounded animate-pulse"></div>
            ) : (
              <p className="text-white font-bold text-lg leading-tight">{s.value}</p>
            )}
            <p className="text-gray-500 text-xs mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { value: "ALL", label: "전체" },
          { value: "charge", label: "충전" },
          { value: "use", label: "사용" },
          { value: "bonus", label: "보너스" },
          { value: "refund", label: "환불" },
          { value: "admin_grant", label: "관리자 지급" },
          { value: "admin_deduct", label: "관리자 차감" },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => { setTypeFilter(f.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
              typeFilter === f.value
                ? "bg-violet-600 text-white"
                : "bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.15]"
            }`}
          >
            {f.label}
            {f.value !== "ALL" && (
              <span className="ml-1.5 opacity-70">
                {transactions.filter((t) => t.type === f.value).length}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={fetchData}
          className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white cursor-pointer transition-colors"
          title="새로고침"
        >
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
        </button>
      </div>

      {/* 내역 목록 */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-14 flex flex-col items-center gap-3">
            <div className="w-7 h-7 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin"></div>
            <p className="text-gray-500 text-sm">내역 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="py-14 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-500/10 mb-1">
              <i className="ri-error-warning-line text-red-400 text-2xl"></i>
            </div>
            <p className="text-red-400 text-sm font-medium">내역을 불러오지 못했어요</p>
            <p className="text-gray-500 text-xs max-w-xs">{error}</p>
            <button
              onClick={fetchData}
              className="mt-2 px-4 py-2 bg-white/[0.06] border border-white/[0.1] rounded-lg text-gray-300 text-xs hover:text-white hover:border-white/[0.2] cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-refresh-line mr-1.5 text-xs"></i>다시 시도
            </button>
          </div>
        ) : paginated.length === 0 ? (
          <div className="py-14 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/[0.04] mb-1">
              <i className="ri-wallet-3-line text-gray-600 text-2xl"></i>
            </div>
            <p className="text-gray-400 text-sm font-medium">
              {typeFilter === "ALL" ? "아직 크레딧 내역이 없어요" : "해당 유형의 내역이 없어요"}
            </p>
            <p className="text-gray-600 text-xs">충전 신청하면 내역이 여기에 표시됩니다</p>
          </div>
        ) : (
          <>
            {/* Desktop: table */}
            <table className="hidden md:table w-full">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                  {["유형", "설명", "금액", "상태", "잔액", "일시"].map((h) => (
                    <th key={h} className="text-left text-gray-500 text-xs font-medium px-5 py-3 whitespace-nowrap first:pl-6">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((tx, idx) => {
                  const cfg = TYPE_CONFIG[tx.type];
                  const st = tx.status ? STATUS_CONFIG[tx.status] : null;
                  const isLast = idx === paginated.length - 1;
                  return (
                    <tr
                      key={tx.id}
                      className={`hover:bg-white/[0.02] transition-colors ${!isLast ? "border-b border-white/[0.04]" : ""}`}
                    >
                      <td className="px-5 py-4 pl-6">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
                            <i className={`${cfg.icon} text-xs`}></i>
                            {cfg.label}
                          </span>
                          {tx.type === "refund" && (
                            <RefundReasonBadge reason={tx.refund_reason} description={tx.description} />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 max-w-[180px]">
                        <p className="text-gray-300 text-sm truncate">{tx.description ?? "-"}</p>
                        {tx.payment_method && (
                          <p className="text-gray-600 text-xs mt-0.5 whitespace-nowrap">
                            {PAYMENT_METHOD_LABELS[tx.payment_method] ?? tx.payment_method}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-sm font-bold whitespace-nowrap ${
                          tx.type === "use" ? "text-red-400" : "text-emerald-400"
                        }`}>
                          {cfg.sign}₩{Math.abs(tx.amount).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {st ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${st.bg} ${st.color}`}>
                            {st.label}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-300 text-sm whitespace-nowrap">
                          ₩{tx.balance_after.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-500 text-xs whitespace-nowrap">
                          {formatDateTime(tx.created_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile: card view */}
            <div className="md:hidden divide-y divide-white/[0.04]">
              {paginated.map((tx) => {
                const cfg = TYPE_CONFIG[tx.type];
                const st = tx.status ? STATUS_CONFIG[tx.status] : null;
                return (
                  <div key={tx.id} className="px-4 py-3.5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-col gap-1.5 items-start">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
                          <i className={`${cfg.icon} text-[10px]`}></i>
                          {cfg.label}
                        </span>
                        {tx.type === "refund" && (
                          <RefundReasonBadge reason={tx.refund_reason} description={tx.description} />
                        )}
                      </div>
                      {st && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${st.bg} ${st.color}`}>
                          {st.label}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-1.5">{tx.description ?? "-"}</p>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-bold ${tx.type === "use" ? "text-red-400" : "text-emerald-400"}`}>
                          {cfg.sign}₩{Math.abs(tx.amount).toLocaleString()}
                        </span>
                        <span className="text-gray-500 text-xs">
                          {formatDateTime(tx.created_at)}
                        </span>
                      </div>
                      <span className="text-gray-500 text-xs">잔액 ₩{tx.balance_after.toLocaleString()}</span>
                    </div>
                    {tx.payment_method && (
                      <p className="text-gray-600 text-[10px] mt-1.5">
                        {PAYMENT_METHOD_LABELS[tx.payment_method] ?? tx.payment_method}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="border-t border-white/[0.06] px-4 md:px-5 py-3 md:py-3.5 flex items-center justify-between">
                <p className="text-gray-500 text-[11px] md:text-xs">{page} / {totalPages} 페이지 · 총 {filtered.length}건</p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 cursor-pointer transition-all"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                    const p = start + i;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-all ${
                          page === p
                            ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                            : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 cursor-pointer transition-all"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 안내 문구 */}
      <div className="flex items-start gap-2 md:gap-3 bg-amber-500/5 border border-amber-500/15 rounded-xl px-3 md:px-4 py-3">
        <i className="ri-information-line text-amber-400 text-sm mt-0.5 shrink-0"></i>
        <p className="text-gray-400 text-[11px] md:text-xs leading-relaxed">
          무통장 입금 충전은 관리자 승인 후 잔액에 반영됩니다.
          승인 대기 중인 금액은 잔액에 포함되지 않습니다.
          환불이 필요한 경우 고객센터로 문의해주세요.
        </p>
      </div>
    </div>
  );
}